import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
} from '@angular/core';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BRAND_CONFIG } from '@core/injection-tokens';
import { MultilingualService } from '@core/services';
import { BrandConfig, CMSContent } from '@models/interfaces';

import { AppCenterAnalytics, AppInsightsService, CmsService } from '@shared/services';

import { LogoComponent } from '@shared/components';
import { SetImagePathDirective } from '@shared/directives';
import { PipesModule } from '@shared/pipes';
import { AppConstants } from '@root/app/app.constants';
import { scrollToSection } from '@shared/utils';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-terms-of-use',
  templateUrl: './terms-of-use.component.html',
  styleUrls: ['./terms-of-use.component.scss'],
  imports: [LogoComponent, SetImagePathDirective, PipesModule],
})
export class TermsOfUseComponent implements OnInit, OnDestroy {
  readonly termsOfUse = signal<CMSContent | null>(null);
  readonly isLoading = signal(false);
  readonly isError = signal(false);
  readonly errMsg = signal<string>('');
  private readonly unlistenFns: (() => void)[] = [];

  constructor(
    private readonly cmsService: CmsService,
    public readonly multilingualService: MultilingualService,
    private readonly appInsightService: AppInsightsService,
    private elRef: ElementRef,
    private renderer: Renderer2,
    private readonly destroyRef: DestroyRef,
    @Inject(BRAND_CONFIG) private brandConfig: BrandConfig
  ) {}

  get brandName(): string {
    return this.brandConfig.displayName;
  }

  ngOnInit(): void {
    this.appInsightService.trackPageView(AppCenterAnalytics.TERMS_OF_USE_VIEWED);
    this.isLoading.set(true);
    this.isError.set(false);
    this.errMsg.set('');
    this.cmsService
      .getTermsOfUse()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: CMSContent) => {
          this.termsOfUse.set(res);
          this.isLoading.set(false);

          // Attach click listeners after content and anchors are present in the DOM.
          requestAnimationFrame(() => {
            this.attachClickEventListeners();
          });
        },
        error: (err: unknown) => {
          this.isLoading.set(false);
          this.isError.set(true);
          this.errMsg.set(String(err));
        },
      });
  }

  // Attach click event listeners after content is loaded
  private attachClickEventListeners(): void {
    this.clearClickEventListeners();

    for (const linkId of AppConstants.TERMS_OF_USE_INTERNAL_LINK_IDs) {
      const anchor = this.elRef.nativeElement.querySelector(`a[href="#${linkId}"]`);
      if (anchor) {
        const unlisten = this.renderer.listen(anchor, 'click', (event) => {
          scrollToSection(linkId);
          event.preventDefault(); // Prevent default anchor behavior (URL update)
        });
        this.unlistenFns.push(unlisten);
      }
    }
  }

  private clearClickEventListeners(): void {
    while (this.unlistenFns.length) {
      const unlisten = this.unlistenFns.pop();
      unlisten?.();
    }
  }

  ngOnDestroy(): void {
    this.clearClickEventListeners();
  }
}
